import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Brain, 
  BookOpen, 
  Clock, 
  Target, 
  Star,
  X,
  AlertCircle 
} from 'lucide-react';
import { ComprehensionAssessment } from './comprehension-assessment';
import { comprehensionService } from '@/services/comprehension-service';
import { useAuth } from '@/components/auth/AuthProvider';

interface ComprehensionAssessmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ComprehensionAssessmentModal({ 
  open, 
  onOpenChange 
}: ComprehensionAssessmentModalProps) {
  const { user } = useAuth();
  const [showIntro, setShowIntro] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  // Check for active session when modal opens
  useEffect(() => {
    if (open && user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
      
      // If there's an active session, skip intro and go directly to assessment
      if (activeSession) {
        setShowIntro(false);
      } else {
        setShowIntro(true);
      }
    }
  }, [open, user?.id]);

  const handleModalOpenChange = (open: boolean) => {
    // If user is trying to close and there's an active session, show pause dialog
    if (!open && user?.id && comprehensionService.hasActiveSession(user.id) && !showIntro) {
      // Allow closing but the session will be saved automatically
      handlePauseAssessment();
    }
    onOpenChange(open);
  };

  const handlePauseAssessment = () => {
    // Assessment is automatically paused (session is saved in the component)
    onOpenChange(false);
    setTimeout(() => {
      setShowIntro(true);
      // Check if there's still an active session after pause
      if (user?.id) {
        const stillHasSession = comprehensionService.hasActiveSession(user.id);
        setHasActiveSession(stillHasSession);
      }
    }, 500);
  };

  const handleStartAssessment = () => {
    setIsStarting(true);
    setTimeout(() => {
      setShowIntro(false);
      setIsStarting(false);
    }, 1000);
  };

  const handleComplete = () => {
    // Clear any saved session on completion
    if (user?.id) {
      comprehensionService.clearAssessmentSession(user.id);
    }
    // Assessment completed successfully
    onOpenChange(false);
    // Reset for next time
    setTimeout(() => {
      setShowIntro(true);
      setHasActiveSession(false);
    }, 500);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setShowIntro(true);
      // Check for active session when closing
      if (user?.id) {
        const activeSession = comprehensionService.hasActiveSession(user.id);
        setHasActiveSession(activeSession);
      }
    }, 500);
  };

  if (showIntro) {
    return (
      <Dialog open={open} onOpenChange={handleModalOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Brain className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  {hasActiveSession ? 'Resume Assessment' : 'Comprehension Assessment'}
                </DialogTitle>
                <DialogDescription className="mt-1">
                  {hasActiveSession 
                    ? 'Continue where you left off' 
                    : 'Help us personalize your learning experience'
                  }
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {/* Resume notification */}
            {hasActiveSession && (
              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  <strong>Assessment in Progress:</strong> You have a paused assessment. Click "Resume Assessment" to continue where you left off, or "Start New" to begin fresh.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-3 text-center">
                  <Target className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h4 className="font-medium text-green-800 text-sm">Personalized Learning</h4>
                  <p className="text-xs text-green-600 mt-1">
                    Get explanations matched to your level
                  </p>
                </CardContent>
              </Card>

              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-3 text-center">
                  <BookOpen className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-medium text-blue-800 text-sm">Better Understanding</h4>
                  <p className="text-xs text-blue-600 mt-1">
                    Learn concepts at your comprehension level
                  </p>
                </CardContent>
              </Card>

              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-3 text-center">
                  <Star className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-medium text-purple-800 text-sm">Improved Results</h4>
                  <p className="text-xs text-purple-600 mt-1">
                    Faster learning with tailored content
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* What to Expect */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center text-sm">
                  <Clock className="h-4 w-4 mr-2 text-primary" />
                  What to Expect
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start space-x-2">
                    <Badge variant="secondary" className="min-w-fit text-xs">1</Badge>
                    <p className="text-sm">Answer 20 multiple-choice questions about different subjects</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Badge variant="secondary" className="min-w-fit text-xs">2</Badge>
                    <p className="text-sm">Select the answer that best matches your understanding level</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Badge variant="secondary" className="min-w-fit text-xs">3</Badge>
                    <p className="text-sm">Each question takes about 1-2 minutes to complete</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Badge variant="secondary" className="min-w-fit text-xs">4</Badge>
                    <p className="text-sm">Your responses help us determine your comprehension level</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Privacy Note */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <strong>Privacy:</strong> Your responses are confidential and only used to personalize your learning experience. 
                You won't see your score, and this assessment will not appear again for 6 months.
              </p>
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="flex-shrink-0 flex justify-end items-center pt-4 border-t">
            <Button 
              onClick={handleStartAssessment}
              disabled={isStarting}
              className="min-w-32"
            >
              {isStarting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Starting...</span>
                </div>
              ) : (
                hasActiveSession ? 'Resume Assessment' : 'Start Assessment'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleModalOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col gap-0">
        <VisuallyHidden>
          <DialogTitle>Assessment Questions</DialogTitle>
          <DialogDescription>Interactive comprehension assessment interface</DialogDescription>
        </VisuallyHidden>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <ComprehensionAssessment 
            onComplete={handleComplete}
            onPause={handlePauseAssessment}
            resumeSession={hasActiveSession}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}